import type { DashboardNavGroup, Domain } from '@routes/pageRoutes.ts';
import type { PlanCapabilities } from '@app-types/site.types.ts';

export interface RouteAccessMeta {
  key?: string;
  requiredApps: readonly string[];
  requiredAnyApps: readonly string[];
  requiredCapabilities: readonly string[];
  featureFlag?: string;
  requiredPermissions: readonly string[];
  requiredGroups: readonly string[];
  requiresStaff: boolean;
  navGroup?: DashboardNavGroup;
  domain?: Domain;
  allowSuperuserWithoutApp?: boolean;
}

export type RouteAccessDeniedReason =
  | 'missing-app'
  | 'missing-any-app'
  | 'missing-capability'
  | 'missing-group'
  | 'missing-permission'
  | 'missing-staff'
  | 'feature-disabled';

export interface RouteAccessDecision {
  allowed: boolean;
  reason?: RouteAccessDeniedReason;
  missingApps?: string[];
  missingCapabilities?: string[];
  missingGroups?: string[];
}

export interface RouteAccessContext {
  apps: readonly string[];
  features: Record<string, boolean | undefined>;
  planCapabilities?: PlanCapabilities;
  isStaff: boolean;
  isSuperuser?: boolean;
  permissions?: readonly string[];
  groups?: readonly string[];
  roles?: readonly string[];
  requiresDomainPermission?: boolean;
  domainPermissions?: readonly string[];
}

const hasCapability = (planCapabilities: PlanCapabilities | undefined, capability: string): boolean => {
  const [namespace, ...rest] = capability.split('.');
  const key = rest.join('.');
  if (!namespace || !key) {
    return false;
  }
  return Boolean(planCapabilities?.[namespace]?.[key]);
};

const normalizeToken = (value: string): string => value.trim().toLowerCase();

const GROUP_TOKEN_ALIASES: Record<string, readonly string[]> = {
  admin: [
    'admin',
    'administrator',
    'owner',
    'owners',
    'staff:admin',
    'staff:owner',
    'staff:owners',
    'erp:staff:admin',
    'erp:owner',
    'erp:owners',
  ],
};

const getAcceptedGroupTokens = (group: string): Set<string> => {
  const normalized = normalizeToken(group);
  const aliases = GROUP_TOKEN_ALIASES[normalized] ?? [normalized];
  return new Set(aliases.map(normalizeToken));
};

export const evaluateRouteAccess = (
  meta: RouteAccessMeta,
  ctx: RouteAccessContext,
): RouteAccessDecision => {
  if (meta.featureFlag && !ctx.features[meta.featureFlag]) {
    return { allowed: false, reason: 'feature-disabled' };
  }

  if (meta.requiresStaff && !ctx.isStaff) {
    return { allowed: false, reason: 'missing-staff' };
  }

  const appSet = new Set(ctx.apps);
  const bypassAddon = Boolean(ctx.isSuperuser && meta.allowSuperuserWithoutApp);
  const missingApps = meta.requiredApps.filter((app) => !appSet.has(app));
  if (missingApps.length > 0) {
    return { allowed: false, reason: 'missing-app', missingApps };
  }

  if (!bypassAddon && meta.requiredAnyApps.length > 0 && !meta.requiredAnyApps.some((app) => appSet.has(app))) {
    return { allowed: false, reason: 'missing-any-app', missingApps: [...meta.requiredAnyApps] };
  }

  const missingCapabilities = bypassAddon ? [] : meta.requiredCapabilities.filter(
    (capability) => !hasCapability(ctx.planCapabilities, capability),
  );
  if (missingCapabilities.length > 0) {
    return { allowed: false, reason: 'missing-capability', missingCapabilities };
  }

  const grantedGroupTokens = new Set(
    [
      ...(ctx.groups ?? []),
      ...(ctx.permissions ?? []),
      ...(ctx.roles ?? []),
    ].map(normalizeToken).filter(Boolean),
  );
  const missingGroups = ctx.isSuperuser
    ? []
    : meta.requiredGroups.filter((group) => {
        const acceptedTokens = getAcceptedGroupTokens(group);
        return !Array.from(acceptedTokens).some((token) => grantedGroupTokens.has(token));
      });
  if (missingGroups.length > 0) {
    return { allowed: false, reason: 'missing-group', missingGroups };
  }

  if (!ctx.isSuperuser) {
    const permissionSet = new Set(ctx.permissions ?? []);
    const hasRequiredPermission =
      meta.requiredPermissions.length === 0 ||
      meta.requiredPermissions.some((permission) => permissionSet.has(permission));
    if (!hasRequiredPermission) {
      return { allowed: false, reason: 'missing-permission' };
    }

    if (ctx.requiresDomainPermission && meta.requiredPermissions.length === 0) {
      const hasDomainPermission = (ctx.domainPermissions ?? []).some((permission) => permissionSet.has(permission));
      if (!hasDomainPermission) {
        return { allowed: false, reason: 'missing-permission' };
      }
    }
  }

  return { allowed: true };
};
